import { VStack, HStack } from '@terra/ui-styled-system';
import { Text } from '../../../components/text';
import { Button } from '../../../components/button';
import { Tooltip } from '../../../components/tooltip';
import { useTranslations } from 'next-intl';
import { getCvssExplanations } from '../constants';
import type { MetricSelectorProps } from '../types';

export function MetricSelector({
  title,
  options,
  value,
  onChange,
  metricKey,
  readOnly,
  footer,
}: MetricSelectorProps) {
  const t = useTranslations();
  const explanation = metricKey
    ? getCvssExplanations(t as (key: string) => string)[metricKey]
    : undefined;

  return (
    <VStack gap={1.5} alignItems="stretch" w="full">
      <Text
        fontSize="xs"
        fontWeight="medium"
        color={readOnly ? 'fg.disabled' : 'fg.muted'}
      >
        {title}
      </Text>
      <HStack gap={1} flexWrap="wrap" alignItems="stretch">
        {options.map((option) => {
          const isSelected = value === option.value;
          const optionExplanation = explanation
            ? explanation.options[
                option.value as keyof typeof explanation.options
              ]
            : undefined;

          const button = (
            <Button
              key={option.value}
              size="xs"
              variant={isSelected ? 'subtle' : 'ghost'}
              onClick={readOnly ? undefined : () => onChange(option.value)}
              disabled={readOnly}
              fontSize="xs"
              px={2}
              py={1}
              minH="7"
              flexShrink={0}
              fontWeight={isSelected ? 'medium' : 'normal'}
              bg={isSelected ? 'bg.muted' : undefined}
              borderWidth="1px"
              borderColor={
                readOnly
                  ? isSelected
                    ? 'border.disabled'
                    : 'border.subtle'
                  : isSelected
                    ? 'border.default'
                    : 'border.subtle'
              }
              color={
                readOnly
                  ? 'fg.disabled'
                  : isSelected
                    ? 'fg.default'
                    : 'fg.muted'
              }
              cursor={readOnly ? 'not-allowed' : undefined}
              opacity={readOnly ? 0.7 : undefined}
              _hover={
                readOnly
                  ? {
                      bg: isSelected ? 'bg.muted' : undefined,
                      borderColor: isSelected
                        ? 'border.disabled'
                        : 'border.subtle',
                      color: 'fg.disabled',
                    }
                  : {
                      bg: isSelected ? 'bg.muted' : 'bg.hover',
                      borderColor: 'border.default',
                    }
              }
              _disabled={{
                opacity: 0.7,
                cursor: 'not-allowed',
                color: 'fg.disabled',
              }}
            >
              {option.label}
            </Button>
          );

          if (!optionExplanation) {
            return button;
          }

          return (
            <Tooltip
              key={option.value}
              openDelay={500}
              positioning={{
                strategy: 'fixed',
                placement: 'top',
              }}
              title={optionExplanation}
              maxW="320px"
            >
              {button}
            </Tooltip>
          );
        })}
      </HStack>
      {footer}
    </VStack>
  );
}
